import React from "react";
import { NumericFormat } from "react-number-format";
import { ErrorMessage, FormikProvider, useFormik } from "formik";
import * as yup from "yup";
import { Button } from "../../../Components/ui/button";
import { ModalClose } from "../../../Components/ui/modal";
import Spinner from "../../../Assets/spinner";

const FeeValidationSchema = yup.object().shape({
  riderFlatFee: yup.number().typeError("Enter a number").min(0, "Must be 0 or more"),
  riderPercentage: yup
    .number()
    .typeError("Enter a number")
    .min(0, "Must be 0 or more")
    .max(100, "Must be 100 or less"),
  serviceFees: yup
    .array()
    .of(
      yup.object().shape({
        orderType: yup.string().required("Required"),
        flatFee: yup.number().typeError("Enter a number").min(0, "Must be 0 or more"),
        percentage: yup
          .number()
          .typeError("Enter a number")
          .min(0, "Must be 0 or more")
          .max(100, "Must be 100 or less"),
      }),
    )
    .min(1, "Add at least one service fee"),
});

const FeeForm = ({ mode = "create", initialValues, submitting, onSubmit }) => {
  const formik = useFormik({
    initialValues,
    validationSchema: FeeValidationSchema,
    onSubmit,
  });

  const { handleSubmit, values, errors, touched, setFieldValue } = formik;

  const updateServiceFee = (index, field, value) => {
    const next = values.serviceFees.map((row, i) =>
      i === index ? { ...row, [field]: value } : row,
    );
    setFieldValue("serviceFees", next);
  };

  return (
    <FormikProvider value={formik}>
      <form onSubmit={handleSubmit}>
        <div className='w-[80vw] md:w-full flex flex-col gap-4'>
          <div>
            <p className='text-sm font-bold text-primaryBlack mb-2'>Rider Fee</p>
            <div className='grid grid-cols-2 gap-3'>
              <div>
                <label className='block text-xs font-semibold text-HeaderColor mb-1.5'>
                  Flat Fee
                </label>
                <NumericFormat
                  className='p-2 outline-none bg-transparent border border-[#C2C7D6] rounded-md w-full placeholder:text-sm'
                  placeholder='₦0'
                  allowNegative={false}
                  decimalScale={2}
                  prefix='₦'
                  thousandSeparator=','
                  value={values.riderFlatFee}
                  onValueChange={({ floatValue }) =>
                    setFieldValue("riderFlatFee", floatValue)
                  }
                />
                {errors.riderFlatFee && touched.riderFlatFee && (
                  <p className='text-xs text-red mt-1'>
                    <ErrorMessage name='riderFlatFee' />
                  </p>
                )}
              </div>
              <div>
                <label className='block text-xs font-semibold text-HeaderColor mb-1.5'>
                  Percentage
                </label>
                <NumericFormat
                  className='p-2 outline-none bg-transparent border border-[#C2C7D6] rounded-md w-full placeholder:text-sm'
                  placeholder='0%'
                  allowNegative={false}
                  decimalScale={2}
                  suffix='%'
                  value={values.riderPercentage}
                  onValueChange={({ floatValue }) =>
                    setFieldValue("riderPercentage", floatValue)
                  }
                />
                {errors.riderPercentage && touched.riderPercentage && (
                  <p className='text-xs text-red mt-1'>
                    <ErrorMessage name='riderPercentage' />
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className='h-px bg-lightGray' />

          <div>
            <p className='text-sm font-bold text-primaryBlack mb-2'>Service Fees</p>

            <div className='flex flex-col gap-3'>
              {values.serviceFees.map((row, index) => (
                <div
                  key={index}
                  className='flex items-start gap-2 border border-lightGray rounded-lg p-3'
                >
                  <div className='flex-1'>
                    <label className='block text-xs font-semibold text-HeaderColor mb-1.5'>
                      Order Type
                    </label>
                    <input
                      className='p-2 outline-none bg-transparent border border-[#C2C7D6] rounded-md w-full text-sm placeholder:text-sm'
                      placeholder='e.g. pickup, delivery, errand'
                      value={row.orderType}
                      onChange={(e) =>
                        updateServiceFee(index, "orderType", e.target.value)
                      }
                    />
                    {errors.serviceFees?.[index]?.orderType &&
                      touched.serviceFees?.[index]?.orderType && (
                        <p className='text-xs text-red mt-1'>
                          {errors.serviceFees[index].orderType}
                        </p>
                      )}
                  </div>
                  <div className='flex-1'>
                    <label className='block text-xs font-semibold text-HeaderColor mb-1.5'>
                      Flat Fee
                    </label>
                    <NumericFormat
                      className='p-2 outline-none bg-transparent border border-[#C2C7D6] rounded-md w-full placeholder:text-sm'
                      placeholder='₦0'
                      allowNegative={false}
                      decimalScale={2}
                      prefix='₦'
                      thousandSeparator=','
                      value={row.flatFee}
                      onValueChange={({ floatValue }) =>
                        updateServiceFee(index, "flatFee", floatValue)
                      }
                    />
                  </div>
                  <div className='flex-1'>
                    <label className='block text-xs font-semibold text-HeaderColor mb-1.5'>
                      Percentage
                    </label>
                    <NumericFormat
                      className='p-2 outline-none bg-transparent border border-[#C2C7D6] rounded-md w-full placeholder:text-sm'
                      placeholder='0%'
                      allowNegative={false}
                      decimalScale={2}
                      suffix='%'
                      value={row.percentage}
                      onValueChange={({ floatValue }) =>
                        updateServiceFee(index, "percentage", floatValue)
                      }
                    />
                  </div>
                </div>
              ))}
            </div>
            {typeof errors.serviceFees === "string" && (
              <p className='text-xs text-red mt-2'>{errors.serviceFees}</p>
            )}
          </div>

          <div className='mt-4 flex justify-between'>
            <ModalClose>
              <Button variant='outline' type='button'>
                Cancel
              </Button>
            </ModalClose>
            <Button type='submit' disabled={submitting}>
              {submitting ? (
                <Spinner />
              ) : mode === "edit" ? (
                "Save Changes"
              ) : (
                "Create Fee"
              )}
            </Button>
          </div>
        </div>
      </form>
    </FormikProvider>
  );
};

export default FeeForm;
